// noinspection DuplicatedCode

import React, { useEffect, useState } from 'react';
import './Partner.scss';

import { Button, Col, Form, FormGroup, Row, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import Contents from 'components/Layout/Contents';
import {
  GroupFormCheck,
  Input,
  InputSocialMedia,
  MarkDownEditor,
  Select,
} from 'components/Forms';
import PartnerService from 'api/PartnerService';
import TierService from 'api/TierService';
import Spinner from 'components/Spinner/Spinner';
import CompetencyService from 'api/CompetencyService';
import UploadImage from 'components/UploadImage/UploadImage';
import Alert from 'components/Alert/Alert';
import useLayout from 'components/Layout/useLayout';

export default function AddPartner() {
  const [specialties, setSpecialties] = useState([]);

  const { scrollToTop } = useLayout();

  const mutationGetSpecialties = useMutation(() => CompetencyService.getAll(), {
    onSuccess: (response) => {
      if (response && response.data) {
        const temp = [];
        response.data.map((sp) => {
          temp.push({
            value: sp.id,
            label: sp.name,
          });

          return sp;
        });
        setSpecialties(temp);
      }
    },
  });

  const tierQuery = useQuery('list-tiers', TierService.listAll, {
    staleTime: 0,
  });

  const [iconSrc, setIconSrc] = useState(null);

  const [tierId, setTierId] = useState('');

  const [tierTemplates, setTierTemplates] = useState([]);

  const statusOptions = [
    {
      value: 'Active',
      label: 'Active',
    },
    {
      value: 'Registered',
      label: 'Registered',
    },
    {
      value: 'Draft',
      label: 'Draft',
    },
  ];

  const { handleSubmit, control, register, watch, setValue } = useForm({
    defaultValues: {
      name: '',
      title: '',
      tierId: '',
      templateId: '',
      description: '',
      email: '',
      phone: '',
      website: '',
      status: '',
      address: {
        street: '',
        suite: '',
        city: '',
        zipCode: '',
        state: '',
        country: '',
      },
      socialMedia: [],
      specialties: [],
    },
  });

  const templateByTierQuery = useQuery(
    ['template-options', tierId],
    () => TierService.getTemplatesById(tierId),
    {
      enabled: false,
      onSuccess: (data) => {
        setTierTemplates(data);
      },
    }
  );

  const resetForm = () => {
    setTimeout(() => {
      setValue('name', '');
      setValue('title', '');
      setValue('tierId', '');
      setValue('templateId', '');
      setValue('description', '');
      setValue('email', '');
      setValue('phone', '');
      setValue('website', '');
      setValue('status', '');
      setValue('file', '');
      setValue('address[street]', '');
      setValue('address[suite]', '');
      setValue('address[city]', '');
      setValue('address[zipCode]', '');
      setValue('address[state]', '');
      setValue('address[country]', '');
      setValue('socialMedia', []);
      setValue('specialties', []);
      setIconSrc(null);
    }, 100);
  };

  const [savedSuccessfully, setSavedSuccessfully] = useState(null);

  const mutationSavePartner = useMutation(
    (newPartner) => PartnerService.add(newPartner),
    {
      onSuccess: (response) => {
        if (response.data) {
          setTimeout(() => resetForm(), 100);
          setSavedSuccessfully(response.data);
          window.history.pushState(
            response.data,
            '',
            `${window.location.href}?saved=${response.data.id}`
          );
          window.scroll({
            top: 0,
            behavior: 'smooth',
          });
        }
      },
    }
  );

  const onSubmit = (formData) => {
    const payload = { ...formData };
    if (formData.socialMedia) {
      let socialMedia = [];
      Object.keys(formData.socialMedia).forEach((element) => {
        const newValue = {
          ...formData.socialMedia[element],
          isVisible: !!formData.socialMedia[element].network,
          network: element,
        };
        socialMedia = [...socialMedia, newValue];
      });

      payload.socialMedia = socialMedia;
    }
    mutationSavePartner.mutate(payload);
  };

  const addPartnerAgain = () => {
    mutationSavePartner.reset();
    setSavedSuccessfully(null);
    window.history.pushState(null, '', '/admin/partners/add');
  };

  const formatAddress = (address) => {
    if (address) {
      const fields = [
        [address.suite],
        [address.street],
        [address.city],
        [address.state],
        [address.country],
        [address.zipCode],
      ];
      const formattedAddress = fields
        .map((part) => part.filter(Boolean).join(' '))
        .filter((str) => str.length)
        .join(', ');

      return formattedAddress;
    }
    return null;
  };

  const renderSavedDetails = () => (
    <div className="saved-details">
      <Alert variant="success" title="Processing">
        New partner successfully added.
      </Alert>
      <div className="d-block mt-4">
        <Table>
          <tbody>
            <tr>
              <td style={{ width: '210px' }} className="text-muted">
                Title
              </td>
              <td>{savedSuccessfully?.title}</td>
              {savedSuccessfully?.logo?.length > 0 && (
                <td rowSpan="8">
                  <img
                    src={savedSuccessfully.logo}
                    alt={savedSuccessfully.title}
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                </td>
              )}
            </tr>
            <tr>
              <td className="text-muted">Name</td>
              <td>{savedSuccessfully?.name}</td>
            </tr>
            <tr>
              <td className="text-muted">Description</td>
              <td data-color-mode="light">
                <MDEditor.Markdown source={savedSuccessfully?.description} />
              </td>
            </tr>
            <tr>
              <td className="text-muted">Phone</td>
              <td>{savedSuccessfully?.phone}</td>
            </tr>
            <tr>
              <td className="text-muted">Email</td>
              <td>{savedSuccessfully?.email}</td>
            </tr>
            <tr>
              <td className="text-muted">Website</td>
              <td>{savedSuccessfully?.website}</td>
            </tr>
            <tr>
              <td className="text-muted">Address</td>
              <td>{formatAddress(savedSuccessfully?.address)}</td>
            </tr>
            <tr>
              <td className="text-muted">Social Media</td>
              <td>
                <Table borderless>
                  <tbody>
                    {savedSuccessfully?.socialMedia?.map((s) => (
                      <tr>
                        <td style={{ width: '20px' }}>
                          <input
                            type="checkbox"
                            readOnly
                            checked={s?.isVisible}
                          />
                        </td>
                        <td
                          style={{
                            width: '100px',
                            textTransform: 'capitalize',
                          }}
                        >
                          {s.network}
                        </td>
                        <td>{s.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </td>
            </tr>
            <tr>
              <td>Specialties</td>
              <td>
                {savedSuccessfully?.specialtiesData?.map((s) => (
                  <span className="btn btn-light me-2">{s.name}</span>
                ))}
              </td>
            </tr>
            <tr>
              <td>Tier</td>
              <td>{savedSuccessfully?.tierName}</td>
            </tr>
            <tr>
              <td>Template</td>
              <td>{savedSuccessfully?.templateId}</td>
            </tr>
            <tr>
              <td>Status</td>
              <td>{savedSuccessfully?.status}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" className="border-0">
                <div className="d-flex py-3">
                  <Link to="/partners">Return to partner list</Link>
                  <Button
                    variant="primary"
                    className="btn btn-primary ms-auto"
                    onClick={addPartnerAgain}
                    role='button'
                    aria-label='Add another partner'
                  >
                    Add another partner
                  </Button>
                </div>
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'tierId') {
        setTierId(value[name]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (tierId.toString().length > 0) {
      setValue('templateId', '');
      templateByTierQuery.refetch();
    }
  }, [tierId]);

  useEffect(() => {
    mutationGetSpecialties.mutate();
    scrollToTop();
  }, []);

  if (mutationSavePartner.isSuccess && savedSuccessfully) {
    return renderSavedDetails();
  }

  return (
    <Contents title="Add Partner">
      {tierQuery.isLoading && <Spinner />}
      {tierQuery.isSuccess && tierQuery.data.data.length === 0 && (
        <Alert
          variant="warning"
          iconProps={{ className: 'me-1' }}
          data-testid="Alert-noTiers"
        >
          Please add a tier first before adding a partner.{' '}
          <Link to="/tiers/add">Click here</Link> to add a tier.
        </Alert>
      )}
      {tierQuery.data?.data.length > 0 && (
        <Form
          onSubmit={handleSubmit(onSubmit)}
          id="frmAddPartner"
          encType="multipart/form-data"
          data-testid="Form-addPartner"
        >
          {mutationSavePartner.isLoading && (
            <Alert
              variant="info"
              title="Processing"
              iconProps={{ spin: true, pulse: true }}
            >
              Adding new partner...
            </Alert>
          )}
          {mutationSavePartner.isError && (
            <Alert variant="danger" title="Error">
              An error occurred: {mutationSavePartner.error.message}
            </Alert>
          )}
          {mutationSavePartner.isSuccess && (
            <Alert variant="success" title="Processing">
              New partner added.
            </Alert>
          )}
          <Row className="mb-sm-3">
            <Col sm={8}>
              <FormGroup>
                <Input
                  name="name"
                  label="Name"
                  control={control}
                  rules={{ required: true }}
                  disabled={mutationSavePartner.isLoading}
                  placeholder="Name"
                />
              </FormGroup>
              <FormGroup>
                <Input
                  name="title"
                  label="Title"
                  control={control}
                  rules={{ required: true }}
                  disabled={mutationSavePartner.isLoading}
                  placeholder="Title"
                />
              </FormGroup>
              <FormGroup>
                <MarkDownEditor
                  name="description"
                  label="Description"
                  control={control}
                  limit={1000}
                  rules={{ required: true }}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Email address"
                  control={control}
                  disabled={mutationSavePartner.isLoading}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  name="phone"
                  label="Phone"
                  type="phone"
                  control={control}
                  placeholder="Phone"
                  disabled={mutationSavePartner.isLoading}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  type="url"
                  name="website"
                  label="Website"
                  control={control}
                  placeholder="Full website url"
                  rules={{ required: true }}
                  disabled={mutationSavePartner.isLoading}
                />
              </FormGroup>
              <FormGroup>
                <div className="form input address">
                  <Row>
                    <Col sm={3}>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        htmlFor="address-street"
                        className="col-form-label"
                      >
                        Address
                      </label>
                    </Col>
                    <Col sm={9}>
                      <div className="d-block mb-3">
                        <Input
                          name="address[street]"
                          control={control}
                          placeholder="Street"
                          disabled={mutationSavePartner.isLoading}
                        />
                      </div>
                      <div className="d-block mb-3">
                        <Input
                          name="address[suite]"
                          control={control}
                          placeholder="Suite"
                          disabled={mutationSavePartner.isLoading}
                        />
                      </div>
                      <div className="d-block mb-3">
                        <Row>
                          <Col sm={6}>
                            <Input
                              name="address[city]"
                              control={control}
                              placeholder="City"
                              disabled={mutationSavePartner.isLoading}
                            />
                          </Col>
                          <Col sm={6}>
                            <Input
                              name="address[zipCode]"
                              control={control}
                              placeholder="Zip / Postal Code"
                              disabled={mutationSavePartner.isLoading}
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="d-block mb-3">
                        <Input
                          name="address[state]"
                          control={control}
                          placeholder="State / Region"
                          disabled={mutationSavePartner.isLoading}
                        />
                      </div>
                      <div className="d-block mb-3">
                        <Input
                          name="address[country]"
                          control={control}
                          placeholder="Country"
                          disabled={mutationSavePartner.isLoading}
                          errorMessage="Country is required"
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </FormGroup>
              <FormGroup>
                <InputSocialMedia
                  label="Social"
                  register={register}
                  disabled={mutationSavePartner.isLoading}
                />
              </FormGroup>
              <FormGroup>
                <div className="d-block specialty-list">
                  {specialties && specialties.length > 0 && (
                    <GroupFormCheck
                      label="Specialties"
                      name="specialties"
                      control={control}
                      items={specialties}
                      register={register}
                      disabled={mutationGetSpecialties.isLoading}
                    />
                  )}
                </div>
              </FormGroup>
            </Col>
            <Col sm={4}>
              <div className="d-flex justify-content-end">
                <UploadImage
                  name="file"
                  title="Logo"
                  register={register}
                  src={iconSrc}
                  setIconSrc={setIconSrc}
                />
              </div>
              <div className="ps-4 ps-lg-4 ps-md-4 ps-sm-0">
                <div className="partner select-container">
                  <Select
                    block
                    label="Tier"
                    name="tierId"
                    options={tierQuery.data?.data}
                    control={control}
                    disabled={tierQuery.isLoading}
                    rules={{ required: true }}
                  />
                </div>
                <Select
                  block
                  label="Template"
                  name="templateId"
                  options={tierTemplates}
                  control={control}
                  disabled={
                    templateByTierQuery.isLoading || tierQuery.isLoading
                  }
                  rules={{ required: true }}
                />

                <Select
                  block
                  label="Status"
                  name="status"
                  options={statusOptions}
                  control={control}
                  rules={{ required: true }}
                />
              </div>
            </Col>
          </Row>
          <FormGroup>
            <Button
              variant="primary"
              className="btn-pmi"
              type="submit"
              disabled={
                mutationSavePartner.isLoading || templateByTierQuery.isFetching
              }
              role='button'
              aria-label='Save'
            >
              Save
            </Button>
            <Link as={Button} to="/partners" className="d-inline-block ms-3">
              Cancel
            </Link>
          </FormGroup>
        </Form>
      )}
    </Contents>
  );
}
